import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FileText, Cpu, Zap } from 'lucide-react';
import { AgentResponse } from '@/lib/agentsApi';
import { renderAiOutput, Citation } from './AiOutput';

/**
 * The "reasoning chain visible" panel promised in IMPLEMENTATION.md §F
 * ("Every agent shows: prompt sent, model used, cost incurred, sources/
 * records cited").
 *
 * Structure:
 *   - Header row: model, cost, citation count
 *   - Output rendered with appropriate shape (subject+body for compose,
 *     answer string for QA, JSON tree for arbitrary)
 *   - Collapsible "Show reasoning" panel with:
 *       · Prompt that was sent (verbatim)
 *       · Citations as clickable chips linking to the underlying record
 *
 * If `response` is null and not pending → placeholder text.
 */

interface AgentRunPanelProps {
  response: AgentResponse | undefined;
  prompt?: string;
  isPending: boolean;
  placeholderKey?: string;
  /** How to render the output. Defaults to a markdown renderer. */
  renderOutput?: (output: unknown) => React.ReactNode;
}

export function AgentRunPanel({
  response,
  prompt,
  isPending,
  placeholderKey,
  renderOutput,
}: AgentRunPanelProps) {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const r = renderOutput || renderAiOutput;

  if (!response && !isPending) {
    return (
      <div className="rounded-md border border-ink-200 bg-white p-5 text-sm">
        <p className="text-ink-500">{t(placeholderKey || 'agents.output_placeholder')}</p>
      </div>
    );
  }

  if (isPending && !response) {
    return (
      <div className="rounded-md border border-ink-200 bg-white p-5 text-sm flex items-center gap-3 text-ink-500">
        <span className="h-4 w-4 rounded-full border-2 border-accent-500/30 border-t-accent-500 animate-spin" />
        {t('agents.thinking')}
      </div>
    );
  }

  if (!response) return null;

  const cents = response.costCents || 0;
  const usd = (cents / 100).toFixed(cents < 100 ? 4 : 2);
  const citationCount = response.citations?.length ?? 0;

  return (
    <div className="rounded-md border border-ink-200 bg-white overflow-hidden">
      {/* Result header — cost / citations. We never expose the
          underlying model name; users see a neutral "AI" or "stub
          mode" badge so we're free to swap providers without a UI
          ripple (and competitors don't get the catalog). */}
      <header className="px-5 py-3 border-b border-ink-200 bg-ink-50 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold">
          <span className="inline-flex items-center gap-1">
            <Cpu className="h-3 w-3" />
            <span>{t('agents.engine_label', { defaultValue: 'AI' })}</span>
          </span>
          {response.model === 'placeholder' && (
            <span
              className="inline-flex items-center gap-1 rounded-md bg-warn-500/10 px-2 py-0.5 text-warn-600 normal-case tracking-normal"
              title={t('agents.placeholder_hint', {
                defaultValue:
                  'AI is not configured — this response is a deterministic stub.',
              })}
            >
              {t('agents.placeholder_badge', { defaultValue: 'stub mode' })}
            </span>
          )}
          <span className="h-3 w-px bg-ink-300" />
          <span className="inline-flex items-center gap-1">
            <Zap className="h-3 w-3" />
            <span className="num">${usd}</span>
          </span>
          {citationCount > 0 && (
            <>
              <span className="h-3 w-px bg-ink-300" />
              <span className="inline-flex items-center gap-1">
                <FileText className="h-3 w-3" />
                <span className="num">
                  {t('agents.citations_label', { count: citationCount })}
                </span>
              </span>
            </>
          )}
        </div>
      </header>

      {/* Output body */}
      <div className="px-5 py-5 text-sm">{r(response.output)}</div>

      {/* Reasoning chain — collapsible */}
      {(prompt || citationCount > 0) && (
        <div className="border-t border-ink-200">
          <button
            type="button"
            onClick={() => setReasoningOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-2.5 text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold hover:bg-ink-50 transition-colors"
            aria-expanded={reasoningOpen}
          >
            <span>{t('agents.reasoning_toggle')}</span>
            <ChevronDown
              className={
                'h-3.5 w-3.5 transition-transform ' + (reasoningOpen ? 'rotate-180' : '')
              }
            />
          </button>

          {reasoningOpen && (
            <div className="px-5 pb-5 pt-1 space-y-4">
              {prompt && (
                <div>
                  <p className="text-[10.5px] uppercase tracking-[0.14em] text-ink-500 font-semibold mb-2">
                    {t('agents.reasoning_prompt')}
                  </p>
                  <pre className="text-[12.5px] font-mono whitespace-pre-wrap text-ink-700 bg-ink-50 border border-ink-200 rounded-md px-3 py-2.5 leading-relaxed">
                    {prompt}
                  </pre>
                </div>
              )}

              {citationCount > 0 && (
                <div>
                  <p className="text-[10.5px] uppercase tracking-[0.14em] text-ink-500 font-semibold mb-2">
                    {t('agents.reasoning_citations')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {response.citations.map((c, i) => (
                      <Citation key={i} kind={c.kind} id={c.id} wsId={wsId} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
